import { Modal } from 'antd'
import { ModalProps } from 'antd/lib/modal'
import _ from 'lodash'
import React from 'react'
import styled from 'styled-components'

import { useViewportSize } from '../../hooks/useViewportSize'
import { Button } from '../Button'
import { CenterChildren } from '../Center'
import { Spin } from '../Spin'
import { Text, Title } from '../Text'

type Props = {
  width?: number | string
  children?: React.ReactNode
  footerDivider?: boolean
  loading?: boolean
} & ModalProps

interface CloseBackgroundProps {
  onClick: (e: any) => void
}

export const ContentFooter = styled.div`
  position: relative;
  margin-top: 40px;
  margin-bottom: -20px;
  padding-top: 20px;

  ::before {
    position: absolute;
    top: 0;
    left: -40px;
    display: block;
    content: '';
    width: calc(100% + 80px);
    height: 1px;
    background-color: ${({ theme }) => theme.colors.bgPrimary};
  }
`

const CloseBackground = styled.div<CloseBackgroundProps>`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 0;
  transform: translate(35%, -34%) rotate(-330deg);
`

const FooterLine = styled.div`
  display: flex;
  .buttons {
    display: flex;
  }
`

const BodyPadding = styled.div<{ loading?: number; footerDivider?: number }>`
  margin: 0 10%;
  padding-top: 20px;
  border-top: ${({ theme, loading }) => (loading ? 'none' : `${theme.colors.borderPrimary} solid 1px`)};
  border-bottom: ${({ theme, footerDivider }) => (footerDivider ? `${theme.colors.borderPrimary} solid 1px` : 'none')};
`

const Footer = ({ okText, okButtonProps, onOk, cancelText, cancelButtonProps, onCancel, footer }: Props) => {
  if (React.isValidElement(footer)) {
    return footer
  }

  return (
    <FooterLine>
      <div className="buttons">
        {!!cancelText && (
          <Button onClick={onCancel} variant="default" size="middle" {...cancelButtonProps}>
            {cancelText}
          </Button>
        )}

        {!!okText && (
          <Button variant="primary" size="middle" onClick={onOk} {...okButtonProps}>
            {okText}
          </Button>
        )}
      </div>
      {!!footer && (
        <Text size="M" bold color="primary" block>
          {footer}
        </Text>
      )}
    </FooterLine>
  )
}

export const Dialog = (props: Props) => {
  const { wrapClassName, title: propTitle, closeIcon, children, footer, onCancel, loading, ...restProps } = props
  const title = loading ? '' : propTitle
  const viewPort = useViewportSize()

  const { isDesktop, isWide } = viewPort || {}

  return (
    <Modal
      {...restProps}
      onCancel={onCancel}
      wrapClassName={`briq-dialog ${wrapClassName}`}
      title={_.isString(title) ? <Title level="medium" center text={title} /> : title}
      closeIcon={closeIcon}
      footer={!_.isNil(footer) ? <Footer {...props} /> : null}
    >
      {(isDesktop || isWide) && (
        <CloseBackground onClick={(e: React.MouseEvent<HTMLButtonElement>) => onCancel && onCancel(e)} />
      )}
      <BodyPadding
        footerDivider={props.footerDivider ? 1 : undefined}
        className="dialog-body-wrapper"
        loading={loading ? 1 : undefined}
      >
        {loading && (
          <CenterChildren pb={3}>
            <Spin delay={250} />
          </CenterChildren>
        )}
        {!loading && children}
      </BodyPadding>
    </Modal>
  )
}
